import { isDefined } from "@clipboard-health/util-ts";
import {
  type GroupChannel,
  GroupChannelListOrder,
  MyMemberStateFilter,
} from "@sendbird/chat/groupChannel";
import sendbirdSelectors from "@sendbird/uikit-react/sendbirdSelectors";
import { type SendbirdChatType } from "@sendbird/uikit-react/types/lib/types";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";

import { APP_V2_APP_EVENTS, logError } from "../../../lib/analytics";
import {
  CHAT_CHANNEL_QUERY_LIMIT,
  FRIEND_CHAT_CUSTOM_TYPE,
  GET_CHAT_CHANNELS_QUERY_KEY,
} from "../../constants";
import { type ChatChannelCustomTypesFilter } from "../../types";

interface FetchChatChannelsParams {
  searchInput?: string;
  customTypesFilter?: ChatChannelCustomTypesFilter;
}

interface GetChatChannelsResult {
  isSdkInitialized: boolean;
  channels: GroupChannel[];
}

export type UseGetChatChannelsProps = FetchChatChannelsParams &
  UseQueryOptions<GetChatChannelsResult, Error>;

export function filterChannelsBySearchInput(
  channels: GroupChannel[],
  searchInput: string
): GroupChannel[] {
  // For facility chats, the channel name is `${facilityName} - ${workerName}` and we want to filter for
  // results that match the facilityName only
  return channels.filter((channel) => {
    const [facilityName] = channel.name.split(" - ");
    return facilityName.toLowerCase().includes(searchInput.toLowerCase());
  });
}

export function getCustomTypesFilterArray(customTypesFilter?: ChatChannelCustomTypesFilter) {
  if (isDefined(customTypesFilter) && customTypesFilter !== "all") {
    if (customTypesFilter === "flex") {
      return [""];
    }

    if (customTypesFilter === "placements") {
      return ["placements"];
    }
  }

  return undefined;
}

function createChannelQuery(
  sdk: SendbirdChatType,
  searchInput?: string,
  customTypesFilter?: ChatChannelCustomTypesFilter
) {
  const customTypesFilterArray = getCustomTypesFilterArray(customTypesFilter);
  return sdk.groupChannel.createMyGroupChannelListQuery({
    includeEmpty: true,
    order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
    limit: CHAT_CHANNEL_QUERY_LIMIT,
    myMemberStateFilter: MyMemberStateFilter.JOINED,
    ...(isDefined(customTypesFilterArray) && { customTypesFilter: customTypesFilterArray }),
    ...(isDefined(searchInput) && { channelNameContainsFilter: searchInput }),
  });
}

export function useGetChatChannels(
  props: UseGetChatChannelsProps = {}
): UseQueryResult<GetChatChannelsResult, Error> {
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(sendBirdState);
  const { searchInput, customTypesFilter, ...options } = props;

  const queryKey = [
    ...GET_CHAT_CHANNELS_QUERY_KEY,
    ...(isDefined(searchInput) ? [searchInput] : []),
    ...(isDefined(customTypesFilter) ? [customTypesFilter] : []),
  ];

  return useQuery({
    queryKey,
    queryFn: async () => {
      if (
        !isDefined(sdk?.groupChannel) ||
        !isDefined(sdk.currentUser) ||
        sdk.connectionState === "CLOSED" ||
        sdk.connectionState === "CONNECTING"
      ) {
        return { isSdkInitialized: false, channels: [] };
      }

      try {
        const query = createChannelQuery(sdk, searchInput, customTypesFilter);
        if (!isDefined(query)) {
          return { isSdkInitialized: true, channels: [] };
        }

        const channels = await query.next();

        // Friends chat is deprecated
        const filteredChannels = channels.filter(
          (channel) => channel.customType !== FRIEND_CHAT_CUSTOM_TYPE
        );

        const finalChannels = searchInput
          ? filterChannelsBySearchInput(filteredChannels, searchInput)
          : filteredChannels;

        return { isSdkInitialized: true, channels: finalChannels };
      } catch (error) {
        logError(APP_V2_APP_EVENTS.GET_CHAT_CHANNELS_FAILURE, {
          error,
          metadata: {
            customTypesFilter,
            searchInput,
            message: error instanceof Error ? error.message : undefined,
          },
        });

        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_CHAT_CHANNELS_FAILURE,
    },
    ...options,
  });
}
